'use client';

import OptionsModelEntity from '@/components/Options/OptionsMode';
import samplesModel from '@/components/Samples/SamplesModel';
import samplesDiagnosticPopupModel from '@/components/Samples/SamplesDiagnosticPopup/SamplesDiagnosticPopupModel';
import { useEffect, useState } from 'react';
import BottomLine from '../BottomLine';
import CoreScript from '../core_script';
import SheetEditorPopup from '../XmlEditorPopup';
import Debug from '../Debug/Debug';
import { notesStatusEvents } from '@/sharedTypes/coreTypes';
import { useSampleSelection } from '@/hooks/useSampleSelection';
import { observer } from 'mobx-react';
import CountDown from '../CountDown';
import { safeGet } from '@/sharedTypes/browserUtils';
import SampleInfo from '../SampleInfo';
import { usePixiEffects } from '@/hooks/usePixiEffects';
import { PiEyeClosedThin } from 'react-icons/pi';
import sheetEditorEntity from '@/entities/SheetEditor/SheetEditor';
import { DndContext } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import SamplesTour from '../Tour/SamplesTour';
import MidiStatus from '../MidiStatus';
import Spinner from '../Spinner/Spinner';
import samplesSelectorModel from '../Samples/SamplesSelector/SamplesSelectorModel';
import { useGameScoring } from '../../hooks/useGamification';
import { getUserInfoFromToken } from '@/utils/authHelpers';
import { useSession } from 'next-auth/react';
import { jwtDecode } from 'jwt-decode';
import user from '@/stores/user';

import { ScoreDisplay } from '../Gamification/ScoreDisplay';
import { FinalScoreModal } from '../Gamification/FinalScoreModal';
import { t } from '@/shared/i18n';
import { SampleLoadError } from '../ErrorStates/SampleLoadError';
import etaLoader from '@/lib/EtaLoader';

function Home() {
  useSampleSelection();
  const { containerRef, effects } = usePixiEffects();
  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);
  const currentSample = samplesSelectorModel.selectedSample;
  const currentSampleId = currentSample?._id || null;
  
  // Get authentication information from multiple sources
  const { data: session } = useSession();
  // Extract user ID - prioritize session.user.sub if available, then decode accessToken
  let currentUserId: string | null = null;
  
  if (session?.user && 'sub' in session.user) {
    currentUserId = (session.user as any).sub;
  } else if (session?.accessToken) {
    try {
      const decoded = jwtDecode(session.accessToken) as any;
      currentUserId = decoded.sub;
    } catch (error) {
      console.error('Error decoding accessToken:', error);
    }
  }
  
  // Fallback to other auth methods
  if (!currentUserId) {
    const userInfoFromToken = getUserInfoFromToken();
    const userFromStore = user.user_info;
    currentUserId = userInfoFromToken?.sub || userFromStore?.sub || null;
  }

  const { 
    gameState, isPerfectRun, resetProgress, isGamificationLoading, gamificationEnabled 
  } = useGameScoring({
    sampleId: currentSampleId,
    userId: currentUserId,
  }); 

  const [showFinalModal, setShowFinalModal] = useState(false);
  // Track error state - read in render body for MobX reactivity
  const hasError = etaLoader.hasError;
  const [showSampleLoadError, setShowSampleLoadError] = useState(false);
  
  useEffect(() => {
    if (hasError) {
      setShowSampleLoadError(true);
    } else {
      setShowSampleLoadError(false);
    }
  }, [hasError]);
  
  // Note effects PIXI event listeners, need later to move it into useGamification
  useEffect(() => {
    const handleNoteSuccess = () => {
      if(!gamificationEnabled) return; // Only trigger effect if gamification is enabled
      // Trigger PIXI.js effect
      if (effects?.showNoteSuccess) {
        effects.showNoteSuccess();
      }
    };

    const handleAllNotesInMeasureSuccess = () => { 
      if(!gamificationEnabled) return; // Only trigger effect if gamification is enabled
      // Trigger PIXI.js effect
      if (effects?.showAllNotesSuccess) {
        effects.showAllNotesSuccess();
      }
    };

    const handleNoteFail = () => {
      if(!gamificationEnabled) return; // Only trigger effect if gamification is enabled
      // Need PIXI effect for shaking the screen a little or showing a red flash
      console.log("Note Failed - consider adding a PIXI effect here besides bubbles");
    };

    // Handle bubble effects from gamification
    const handleShowNoteBubble = (e: Event) => {
      if (!gamificationEnabled) return; // Only show bubbles if gamification is enabled
      
      const { scoreDelta } = (e as CustomEvent).detail;
      if (effects?.showNoteBubble) {
        // Generate random position on screen
        const x = Math.random() * (window.innerWidth * 0.8) + (window.innerWidth * 0.1); // 10% margin on sides
        const y = Math.random() * (window.innerHeight * 0.6) + (window.innerHeight * 0.2); // 20% margin top/bottom
        effects.showNoteBubble(x, y, scoreDelta);
      }
    };

    // Handle practice end effects from gamification
    const handleShowPracticeEnd = (e: Event) => {
      if (!gamificationEnabled) return; // Only show effects if gamification is enabled
      
      const { scoreDelta } = (e as CustomEvent).detail;
      if (effects?.showPracticeEnd) {
        effects.showPracticeEnd(scoreDelta);
      }
    };

    // Always listen to basic note events for PIXI effects
    window.addEventListener(notesStatusEvents.NOTE_SUCCESS, handleNoteSuccess);
    window.addEventListener(notesStatusEvents.ALL_NOTES_IN_MEASURE_FINISHED, handleAllNotesInMeasureSuccess);
    window.addEventListener(notesStatusEvents.NOTE_FAILED, handleNoteFail);
    
    // Always listen to gamification events - handlers will check if enabled
    window.addEventListener('showNoteBubble', handleShowNoteBubble);
    window.addEventListener('showPracticeEnd', handleShowPracticeEnd);
    
    return () => {
      // Always clean up all listeners
      window.removeEventListener(notesStatusEvents.NOTE_SUCCESS, handleNoteSuccess);
      window.removeEventListener(notesStatusEvents.ALL_NOTES_IN_MEASURE_FINISHED, handleAllNotesInMeasureSuccess);
      window.removeEventListener(notesStatusEvents.NOTE_FAILED, handleNoteFail);
      window.removeEventListener('showNoteBubble', handleShowNoteBubble);
      window.removeEventListener('showPracticeEnd', handleShowPracticeEnd);
    };
  }, [effects, gamificationEnabled]);

  const handleOutsideClick = (event: any) => {
    const optionsRef: any = OptionsModelEntity.optionsRef;
    const optionsBtnRef: any = OptionsModelEntity.optionsBtnRef;
    const samplesRef: any = samplesModel.ref;
    const samplesBtnRef: any = samplesModel.samplesModelBtn;
    const samplesWarningRef = samplesDiagnosticPopupModel.ref;

    let clickedOutside = true;

    const elementsToCheck = [
      optionsRef,
      samplesRef,
      samplesBtnRef,
      optionsBtnRef,
      samplesWarningRef,
    ];

    const { clientX, clientY } = event;

    for (const ref of elementsToCheck) {
      if (ref && ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const isInside =
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom;

        if (isInside) {
          clickedOutside = false;
          break;
        }
      }
    }

    if (clickedOutside) {
      closeAllTabs();
    }
  };

  useEffect(() => {
    const handlePracticeEnd = () => {
      if (gamificationEnabled) {
        setShowFinalModal(true);
      }
    };

    window.addEventListener(notesStatusEvents.PRACTICE_END, handlePracticeEnd);
    return () => {
      window.removeEventListener(notesStatusEvents.PRACTICE_END, handlePracticeEnd);
    };
  }, [gamificationEnabled]);

  const handleReplay = () => {
    setShowFinalModal(false);
    resetProgress();
  };

  return (
    <div
      className='min-h-screen w-full flex flex-col'
      onKeyDown={(e) => {
        globalKeyPressed(e);
      }}
    >
      {/* PIXI.js effects container */}
      <div ref={containerRef} className="fixed inset-0 pointer-events-none z-50" />

      {/* Full-page error when sample loading fails and no sample is loaded */}
      {showSampleLoadError && (
        <SampleLoadError />
      )}

      {/* Score Display - only show if gamification is enabled */}
      {gamificationEnabled && (
        <>
          {/* Show loading if: gamification is loading OR we have userId but no sample yet (sample loading) */}
          {(isGamificationLoading || (currentUserId && !currentSample)) ? (
            <div className="fixed top-4 right-4 text-black">{t('gamification.score_loading')}</div>
          ) : !currentSample ? (
            <div className="fixed top-4 right-4 text-black">{t('gamification.no_selected_sample')}</div>
          ) : (
            <ScoreDisplay
              currentScore={gameState.currentScore}
              highScore={gameState.highScore}
              multiplier={gameState.multiplier}
              isAuthenticated={!!currentUserId}
            />
          )}
        </>
      )}

      {/* Progress Modal */}
      {/* {showProgressModal && savedProgress && (
        <ProgressModal
          sampleTitle={currentSample.title}
          savedProgress={savedProgress}
          onContinue={handleContinue}
          onRestart={handleRestart}
        />
      )} */}

      {/* Final Score Modal - only show if gamification is enabled */}
      {gamificationEnabled && (
        <FinalScoreModal
          open={showFinalModal}
          onOpenChange={setShowFinalModal}
          finalScore={gameState.currentScore}
          highScore={gameState.highScore}
          isNewRecord={gameState.currentScore > gameState.highScore && gameState.highScore > 0}
          isPerfect={isPerfectRun}
          onReplay={handleReplay}
        />
      )}
      
      <DndContext
        onDragEnd={(event) => {
          if (event.active.id === 'popup' && event.delta) {
            sheetEditorEntity.updatePopupPosition(event.delta);
          }
        }}
        modifiers={[restrictToWindowEdges]}
      >
        <div className='flex-auto w-full flex flex-col'>
          <SampleInfo />
          <CoreScript />
          <CountDown />
          <Debug />
          {sheetEditorEntity.noteEditingPopupVisible && <SheetEditorPopup />}
        </div>
        {safeGet('rendererSheetModel') && safeGet('rendererSheetModel.hidden') && (
          <div className='fixed inset-0 z-50 bg-background flex items-center justify-center pointer-events-none'>
            <PiEyeClosedThin size={256} />
          </div>
        )}
        <div className='hidden md:block'>
          <SamplesTour />
        </div>
      </DndContext>
      <Spinner></Spinner>
      <BottomLine />
      {/* <MidiHelper managerEntity={managerEntity} /> */}
      <MidiStatus />
    </div>
  );
  //   return (
  //     <div
  //       className='max-h-screen w-screen flex flex-col'
  //       onKeyDown={(e) => {
  //         globalKeyPressed(e);
  //       }}
  //     >
  //       <div className='flex-auto w-full overflow-y-auto'>
  //         <SampleInfo />
  //         {safeGet('rendererSheetModel') && (
  //           <div className='w-full flex items-center justify-center'>
  //             {safeGet('rendererSheetModel.hidden') && (
  //               <div className='fixed inset-0 z-50 bg-background flex items-center justify-center'>
  //                 <PiEyeClosedThin size={256} />
  //               </div>
  //             )}
  //           </div>
  //         )}
  //         <CoreScript />
  //         <CountDown />
  //         <Debug />
  //         <SheetEditorPopup />
  //       </div>
  //       <BottomLine />
  //     </div>
  //   );
}

function globalKeyPressed(e: any) {
  switch (e.key) {
    case 'Escape':
      closeAllTabs();
      break;
    default:
      break;
  }
}

function closeAllTabs() {
  OptionsModelEntity.showOptions = false;
  samplesModel.samplesHidden = true;
}

export default observer(Home);
